import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {ActivatedRoute, Params, Router} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';

import {EMPTY, forkJoin, Observable, of, throwError} from 'rxjs';
import {catchError, debounceTime, filter, finalize, map, switchMap, takeUntil, tap} from 'rxjs/operators';

import cloneDeep from 'lodash-es/cloneDeep';
import isEmpty from 'lodash-es/isEmpty';
import isObject from 'lodash-es/isObject';

import {TreeRootResponse} from '../../../+organizations/org-interfaces';
import {OrgTreeService, responseCallbackToTreeNodes} from '../../../+organizations/org-tree.service';
import {AdministrationService} from '../../../+settings/administration/administration.service';
import {Destroyable} from '../../../destroyable.component';
import {ApiService} from '../../api';
import {ModalCommand, ModalService, ModalState} from '../../modal';
import {DefaultPagination, Pagination, TreeNode} from '../../ui';
import {createFiltersObj} from '../../utils/payroll-filters';
import {AccountingUtilsService} from '../accounting-utils.service';
import {
  AccountingPlan,
  AccountingRule,
  AccountingRuleVariability,
  AccountingRuleView,
  RegContractIndexCriteria
} from '../accounting.model';
import {AccountingService} from '../accounting.service';

export const filterList = (params, accountingRules) => {
  const accountingRulesList = [...accountingRules];
  if (Object.keys(params).every(key => isEmpty(params[key]))) {
    return accountingRulesList;
  }
  return accountingRulesList
    .filter(value => !params.account || value?.account?.toLowerCase().includes(params.account.toLowerCase().trim()))
    .filter(value => !params.name || value?.accountName?.toLowerCase().includes(params.name.toLowerCase().trim()))
    .filter(value => !params.accountType || value?.accountType?.toLowerCase().includes(params.accountType.toLowerCase().trim()))
    .filter(value => !params.debitRules || value?.debitRules?.some(v => {
      if (isObject(v)) {
        return v.value.toLowerCase().includes(params.debitRules.toLowerCase().trim())
          || v.display.toLowerCase().includes(params.debitRules.toLowerCase().trim());
      }
      return v.toLowerCase().includes(params.debitRules.toLowerCase().trim());
    }))
    .filter(value => !params.creditRules || value?.creditRules?.some(v => {
      if (isObject(v)) {
        return v.value.toLowerCase().includes(params.creditRules.toLowerCase().trim())
          || v.display.toLowerCase().includes(params.creditRules.toLowerCase().trim());
      }
      return v.toLowerCase().includes(params.creditRules.toLowerCase().trim());
    }))
    .filter(value => !params.complementaryContractName
      || value?.complementaryContractsList?.some(v => v.name.toLowerCase().includes(params.complementaryContractName.toLowerCase().trim())))
    .sort((a, b) => {
      if (a.active && !b.active) {
        return -1;
      } else if (!a.active && b.active) {
        return 1;
      } else {
        return a.index - b.index;
      }
    });
};

@Component({
  selector: 'gp-accounting-mapping-component',
  templateUrl: './accounting-mapping.component.html',
  styleUrls: ['./accounting-mapping.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountingMappingComponent extends Destroyable implements OnInit, OnChanges, OnDestroy {

  @Input() public readOnly = false;
  @Input() accountingPlan: AccountingPlan;
  @Input() cliRegIds: Array<number> = [];
  @Input() defaultPayArrayNames: string[];
  @Output() errorEvent: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('container', {static: false}) tableContainer: ElementRef;

  @ViewChildren('accountingRuleAccountInput')
  public accountingRuleAccountInput: QueryList<ElementRef>;

  @ViewChildren('unClickableZone')
  public unClickableZone: QueryList<ElementRef>;

  public initialAccountingRules: Array<AccountingRuleView> = [];
  public payArraysForCreditRules: { display: string, value: string }[] = [];
  public payArraysForDebitRules: { display: string, value: string }[] = [];
  public originalPayArrayList: { display: string, value: string }[] = [];
  public accountingRuleForm: UntypedFormGroup;
  public deletedRules: Array<AccountingRule> = [];
  public complementaryContractEntity: TreeRootResponse;
  public complementaryContractList: { id: number, name: string }[] = [];
  public complementaryContractTree: TreeNode<any>[] = [];

  public paramOptions: RegContractIndexCriteria[] = [];
  public operatorOptions = [
    {value: 'EQUALS', display: '='},
    {value: 'NOT_EQUALS', display: '<>'}
  ];
  public trueFalseOptions = ['true', 'false'];

  public executingActions: boolean;

  public pagination: Pagination = new DefaultPagination();
  public dataLoaded: EventEmitter<void> = new EventEmitter<void>();
  public isAccountingRuleInitialised = false;
  public isAccountingRuleLoading = false;
  public isStandAloneView = false;
  public colSpanTable: number;

  public filtersVisible = false;
  public filtersGroup: UntypedFormGroup;

  private perPage = 10;
  private complementaryContractName = 'ContratComplementaire';
  private organisationTreeName = 'organization';

  private removeClickOutsideListener: Function;

  public submitCallback: () => Promise<void>;
  public accountingRuleSelection: AccountingRuleView;
  public selectedRegContractIndex: RegContractIndexCriteria[] = [];
  public isSelectedRegContractIndexLoading = false;
  public cliRegId: number;
  public enableVariabilities = false;

  private lastEditedIndex: number;
  // State before modification (for cancellation)
  private initialVariabilities: AccountingRuleVariability[];

  constructor(private cd: ChangeDetectorRef,
              private api: ApiService,
              public formBuilder: UntypedFormBuilder,
              private renderer: Renderer2,
              private route: ActivatedRoute,
              private router: Router,
              private modalService: ModalService,
              private translate: TranslateService,
              private administrationService: AdministrationService,
              private accountingService: AccountingService,
              private orgTreeService: OrgTreeService) {
    super();
    this.isAccountingRuleLoading = true;
    this.calculateColSpan();
    this.filtersGroup = this.createFilterFormGroups();
    this.pagination.perPage = this.perPage;
    this.accountingRuleForm = this.formBuilder.group({
      accountingRules: this.formBuilder.array([])
    });
    this.submitCallback = this.submitVariabilities.bind(this);
  }

  public ngOnInit() {
    this.route.params.pipe(
      filter(urlParams => urlParams.accountingPlanId),
      switchMap((urlParams: Params) => {
        this.defaultPayArrayNames = AccountingUtilsService.originalPayArrayNames;
        this.isStandAloneView = true;
        return this.accountingService.getAccountingPlan(urlParams.accountingPlanId).pipe(
          catchError(error => {
            if (error.status === 404) {
              return of(null);
            }
            throw error;
          })
        );
      }),
      switchMap((accountingPlan: AccountingPlan) => {
        if (!accountingPlan) {
          this.router.navigate(['configuration/accounting']);
        }
        this.accountingPlan = accountingPlan;
        return this.route.queryParams;
      }),
      tap((queryParams: Params) => {
        this.cliRegIds = queryParams['cliRegIds']
          .split(',')
          .map(function(item) {
            return parseInt(item, 10);
          });
        const readOnlyParam = queryParams['readOnly'];
        this.readOnly = readOnlyParam ? readOnlyParam.toLocaleLowerCase() === 'true' : false;
        this.updateIfRequired();
      })
    ).subscribe();

    this.isSelectedRegContractIndexLoading = true;
    this.api.silent.get('/payroll/config').pipe(
      filter(response => {
        this.enableVariabilities = response['gp.misc.display.variabilities'];
        return this.enableVariabilities;
      }),
      switchMap(() => this.administrationService.getCliRegData()),
      tap((regs) => {
        // get cli reg international (root)
        this.cliRegId = regs.data.find(reg => !reg.attributes.parentreg)?.id;
      }),
      switchMap(() => this.accountingService.getRegContractIndexCriteria()),
      tap(resp => this.paramOptions = resp),
      switchMap(() => this.accountingService.getEmpZone(this.cliRegId)),
      tap(resp =>
        this.paramOptions.map(
          param => param.empZone = resp?.find(
            empZone => empZone.name === param.name
          )
        )
      ),
      finalize(() => {
        this.isSelectedRegContractIndexLoading = false;
        this.cd.detectChanges();
      })
    ).subscribe();
    this.updateIfRequired();
  }

  public ngOnChanges(): void {
    this.updateIfRequired();
  }

  public updateIfRequired() {
    if (this.accountingPlan) {
      this.isAccountingRuleInitialised = false;
      this.isAccountingRuleLoading = true;

      this.orgTreeService.getTreeRoots(this.organisationTreeName)
        .pipe(
          switchMap(treeRootResponse => {
            this.complementaryContractEntity = treeRootResponse.find(tree => tree.name === this.complementaryContractName);
            if (!this.complementaryContractEntity) {
              return throwError(this.translate.instant('ORGANIZATION.no_tree_found_for_type',
                {treeType: this.complementaryContractName}));
            }
            return of(treeRootResponse);
          }),
          switchMap(() => this.orgTreeService.getParentsTree(this.complementaryContractName)
            .pipe(
              tap(contractTree => {
                this.complementaryContractList = contractTree;
                this.complementaryContractTree = responseCallbackToTreeNodes(contractTree, new Map());
                this.isAccountingRuleInitialised = true;
                this.cd.detectChanges();
              }))
          ),
          switchMap(() => this.fetchAccountingRulesBasedOnAccountingPlan()),
          tap(() => {
            if (!this.readOnly && this.cliRegIds.length !== 0) {
              this.populatePayArrays();
            }
          }),
          catchError((err) => {
            this.modalService.alert({
              title: this.translate.instant('COMMON.alert'),
              text: '' + err
            }).then(() => {
              this.errorEvent.next(err);
            });
            return EMPTY;
          })
        )
        .subscribe();

      this.filtersGroup.valueChanges
        .pipe(
          debounceTime(400),
          tap(() => this.filterAccountingRulesBasedOnInput()),
          takeUntil(this.componentDestroyed$)
        )
        .subscribe();
    }
  }

  public addNewAccountingRule(): void {
    this.increasePaginationCount();
    const insertionIndex = this.goToRelevantPageAndGetInsertionIndex();
    const control = this.accountingRules;
    const newAccountingRuleView = this.createNewAccountingRule(insertionIndex);
    this.initialAccountingRules.push(newAccountingRuleView);
    const newRuleFormControl = this.createFromGroupFromAccountingRule(newAccountingRuleView);
    control.push(newRuleFormControl);
    this.disableEditModeForUnSelectedRows(control.length - 1);
    this.setFocusToNewRow(control.length - 1);
  }

  public copyAccountingRule(event: MouseEvent, accountingRule: AccountingRuleView, formIndex: number): void {
    event.stopPropagation();
    this.disableEditMode();
    this.increasePaginationCount();
    const formControlIndex = accountingRule.index + 1;
    this.lastEditedIndex = formControlIndex;
    this.goToNextPageIfNecessary(formIndex + 1);
    const newAccountingRule = cloneDeep(accountingRule);
    newAccountingRule.id = null;
    newAccountingRule.newRule = true;
    newAccountingRule.index = formControlIndex;
    this.initialAccountingRules.splice(formControlIndex, 0, newAccountingRule);
    this.reCalculateIndex();
    const control = this.accountingRules;
    setTimeout(() => {
      control.insert(formIndex + 1, this.createFromGroupFromAccountingRule(newAccountingRule));
      control.at(formIndex + 1).markAllAsTouched();
      newAccountingRule.newRule = true;
      control.at(formIndex + 1).patchValue(newAccountingRule);
      this.disableEditMode();
      this.cd.detectChanges();
    });
  }

  public deleteAccountingRules(event: MouseEvent, accountingRule: AccountingRuleView): void {
    event.stopPropagation();
    const control = this.accountingRules;
    this.initialAccountingRules = this.initialAccountingRules.filter(ar => ar.index !== accountingRule.index);
    const retrievedFormIndex = control.controls.findIndex(arFrmGrp => arFrmGrp.value.index === accountingRule.index);
    if (retrievedFormIndex >= 0) {
      control.removeAt(retrievedFormIndex);
    }
    this.decreasePaginationCount();
    if (!accountingRule.newRule) {
      this.deletedRules.push(accountingRule);
    }
    this.reCalculateIndex();
    this.filterAccountingRulesBasedOnInput();
    this.cd.detectChanges();
  }

  public editCell(accountingRule: AccountingRuleView, formIndex: number): void {
    const formControlIndex = this.getActualIndexBasedOnCurrentPage(formIndex);
    if (!this.readOnly) {
      accountingRule.editMode = true;
      this.lastEditedIndex = accountingRule.index;
      const control = this.accountingRules;
      control.markAllAsTouched();
      control.at(formControlIndex).patchValue(accountingRule);
      this.disableEditModeForUnSelectedRows(formControlIndex);
      this.cd.detectChanges();
    }
  }

  public getActualIndexBasedOnCurrentPage(formIndex: number) {
    return (this.pagination.currentPage.number * this.pagination.perPage) + formIndex;
  }

  public addClickOutsideListener(): void {
    this.removeClickOutsideListener = this.renderer.listen('document', 'click', (event) => {
      if (event.target && document.contains(event.target) && !this.tableContainer.nativeElement.contains(event.target)
        && this.unClickableZone.filter(el => el.nativeElement.contains(event.target)).length === 0) {
        this.disableEditMode();
      }
    });
  }

  public confirmResetForm(): void {
    this.lastEditedIndex = null;
    this.modalService.confirm({
      'title': 'DIALOG.cancel_pending_changes',
      'text': 'DIALOG.cancel_pending_changes?',
      'customClass': '',
      'type': 'warning'
    }).then((confirmed: boolean) => {
      if (confirmed) {
        this.filtersGroup.reset();
        this.populatePayArrays();
        this.fetchAccountingRulesBasedOnAccountingPlan().subscribe();
        this.cd.detectChanges();
      }
    });
  }

  public updateAccountingRules(): void {
    this.disableEditMode();
    this.saveAccountingRule(this.accountingRuleSelection);
  }

  private saveAccountingRule(singleAccountingRule?: AccountingRuleView) {
    const actions: Observable<any>[] = [];

    let accountingRuleViewsToBeSaved: AccountingRuleView[];
    if (singleAccountingRule) {
      accountingRuleViewsToBeSaved = [this.initialAccountingRules.find(ar => ar.id === singleAccountingRule.id)];
      accountingRuleViewsToBeSaved[0].accountingRuleVariabilities = singleAccountingRule.accountingRuleVariabilities;
    } else {
      accountingRuleViewsToBeSaved = [...this.initialAccountingRules];
    }

    if (this.accountingRuleForm.valid) {
      accountingRuleViewsToBeSaved
        .map(ar => {
          ar.complementaryContractIds = (ar.complementaryContractsList || []).map(compContracts => compContracts.id);
          const tempDebitRulesTagModel = [...ar.debitRules || []];
          const tempCreditRulesTagsModel = [...ar.creditRules || []];
          const accountingRuleToBeSaved = cloneDeep(ar);
          accountingRuleToBeSaved.debitRules = this.mapTagModelsToRule(tempDebitRulesTagModel);
          accountingRuleToBeSaved.creditRules = this.mapTagModelsToRule(tempCreditRulesTagsModel);
          accountingRuleToBeSaved.accountingRuleVariabilities = ar.accountingRuleVariabilities;
          return <AccountingRule>accountingRuleToBeSaved;
        })
        .map(accountingRule => {
          return actions.push(
              this.accountingService.saveAccountingRule(
                this.accountingPlan?.id,
                accountingRule
              )
            );
          }
        );
    }

    if (this.deletedRules.length) {
      this.deletedRules.map(accountingRule => actions.push(
        this.accountingService.deleteAccountingRule(this.accountingPlan?.id, accountingRule.id)));
    }

    if (actions.length > 0) {
      this.executingActions = true;
      this.accountingRuleForm = this.formBuilder.group({accountingRules: this.formBuilder.array([])});
      forkJoin(actions)
        .pipe(
          finalize(() => {
            this.dataLoaded.next(null);
            this.fetchAccountingRulesBasedOnAccountingPlan().subscribe();
            this.executingActions = false;
            this.isAccountingRuleLoading = false;
          })
        ).subscribe();
    }
  }

  get accountingRules(): UntypedFormArray {
    return <UntypedFormArray>this.accountingRuleForm.get('accountingRules');
  }

  public canSaveButtonBeEnabled(): boolean {
    const formControl = this.accountingRuleForm.get('accountingRules') as UntypedFormArray;
    return formControl.valid && !this.isAccountingRuleLoading
      && this.formContainsChanges();
  }

  public formContainsChanges(): boolean {
    const containsNewRuleOrModifiedRule = this.initialAccountingRules.filter(rule => rule.newRule || rule.modified).length > 0;
    const containsDeletedRules = this.deletedRules.length > 0;

    return containsNewRuleOrModifiedRule || containsDeletedRules || !isEmpty(this.getDirtyValues(this.accountingRules));
  }

  public toggleValue(accountingRule: AccountingRuleView, formIndex: number) {
    this.initialAccountingRules[accountingRule.index] = accountingRule;
    if (!this.readOnly) {
      accountingRule.active = !accountingRule.active;
      const control = this.accountingRules;
      control.at(formIndex).patchValue(accountingRule);
    }
  }

  public filterDebitRulesBasedOnInput(input: string) {
    this.payArraysForDebitRules = this.filterPayArraysBasedOnInput(input, this.payArraysForDebitRules);
  }

  public filterCreditRulesBasedOnInput(input: string) {
    this.payArraysForCreditRules = this.filterPayArraysBasedOnInput(input, this.payArraysForCreditRules);
  }

  public ngOnDestroy() {
    if (this.removeClickOutsideListener) {
      this.removeClickOutsideListener();
    }
  }

  public getPaginationStartIndex(): number {
    return this.pagination.currentPage.number * this.pagination.perPage;
  }

  public getPaginationEndIndex(): number {
    const currentPageNumber = this.pagination.currentPage.number + 1;
    return currentPageNumber * this.pagination.perPage;
  }

  public formContainsEditableRows() {
    return this.accountingRules.controls.filter(x => x.value.editMode).length === 0;
  }

  public onTreeSelection(control: AbstractControl, event) {
    if (event.id) {
      const value = control.value;
      if (!value.find(complementaryContract => complementaryContract.id === event.id)) {
        control.markAsTouched();
        control.markAsDirty();
        value.push({
          id: event.id,
          name: event.text
        });
        control.patchValue(value);
      }
    }
  }

  private createFilterFormGroups() {
    return this.formBuilder.group({
      account: [''],
      name: [''],
      accountType: [''],
      debitRules: [''],
      creditRules: [''],
      complementaryContractName: ['']
    });
  }

  private disableEditMode() {
    if (this.lastEditedIndex !== null && this.lastEditedIndex >= 0) {
      const tempIndex = this.lastEditedIndex;
      const control = this.accountingRuleForm.get('accountingRules') as UntypedFormArray;
      const index = this.initialAccountingRules.findIndex(ar => ar.index === tempIndex);
      const formControlIndex = control.controls.findIndex(ctr => ctr.value.index === tempIndex);
      const rule = control.at(formControlIndex);
      if (rule) {
        rule.value.editMode = false;
        rule.value.modified = rule.dirty;
        rule.patchValue(rule.value);
        const newValue = {...rule.value};
        newValue.index = index;
        this.initialAccountingRules[index] = newValue;
        this.lastEditedIndex = null;
      }
      this.filterAccountingRulesBasedOnInput();
      this.cd.detectChanges();
    }
  }

  private createFromGroupFromAccountingRule(accountingRule: AccountingRuleView) {
    return this.formBuilder.group({
      id: [accountingRule.id, ''],
      accountName: [accountingRule.accountName, Validators.required],
      account: [accountingRule.account, [Validators.required, this.uniqueStringValidator()]],
      accountType: [accountingRule.accountType, ''],
      debitRules: [accountingRule.debitRules],
      creditRules: [accountingRule.creditRules],
      complementaryContractsList: [accountingRule.complementaryContractsList || (accountingRule.complementaryContractIds || [])
        .map(id => this.complementaryContractList.find(node => node.id === id))],
      active: [{value: accountingRule.active, disabled: this.readOnly}],
      editMode: [accountingRule.editMode, ''],
      modified: [accountingRule.modified, ''],
      newRule: [accountingRule.newRule, ''],
      accountingRuleVariabilities: [accountingRule.accountingRuleVariabilities, ''],
      index: [accountingRule.index, '']
    });
  }

  private populatePayArrays() {
    const actions: Observable<any>[] = [];
    this.payArraysForCreditRules = [];
    this.payArraysForDebitRules = [];
    this.originalPayArrayList = [];
    const payArrayNames = this.accountingPlan?.payArrayNames?.length ?
      this.accountingPlan.payArrayNames : this.defaultPayArrayNames;
    const filterPayArrays = this.createPayArrayFilterString(payArrayNames);
    this.cliRegIds.forEach(cliRegId => {
      actions.push(this.accountingService.getPayArrays(cliRegId, filterPayArrays));
    });

    if (actions.length > 0) {
      forkJoin(actions)
        .pipe(
          tap(value => {
            const columnContents = value.flatMap(payArray => payArray
              .map(item => item.columnContent.split('.')[0]));
            const translatedUniqueArray = [...new Set(columnContents)];
            translatedUniqueArray.forEach(arr => {
              this.originalPayArrayList.push({display: this.translate.instant(arr), value: arr});
            });
            this.payArraysForDebitRules = [...this.originalPayArrayList];
            this.payArraysForCreditRules = [...this.originalPayArrayList];
          }),
          catchError(() => {
            return EMPTY;
          }),
          takeUntil(this.componentDestroyed$))
        .subscribe();
    }
  }

  private createPayArrayFilterString(arrayNames: string[]) {
    const columnNames = ['Amount', 'AmountCompany', 'AmountEmployee'];
    const quotedArrayNames = arrayNames.map(arrayName => '\'' + arrayName + '\'');
    const quotedColumnNames = columnNames.map(columnName => '\'' + columnName + '\'');

    return `?filter=@.arrayName in
     [${quotedArrayNames}] ${encodeURIComponent('&&')}
       @.columnType=='RULE'${encodeURIComponent('&&')}@.columnName in [${quotedColumnNames}]`;
  }

  private createAccountingRuleFormControlFromAccountingRules(rules: AccountingRuleView[]) {
    this.accountingRuleForm = this.formBuilder.group({accountingRules: this.formBuilder.array([])});
    const formArray = this.accountingRules;
    rules.forEach(accountingRule => {
      const grp = this.createFromGroupFromAccountingRule(accountingRule);
      grp.markAllAsTouched();
      formArray.push(grp);
    });
  }

  private increasePaginationCount() {
    this.pagination.update(this.pagination.totalItems += 1);
  }

  private decreasePaginationCount() {
    this.pagination.update(this.pagination.totalItems -= 1);
  }

  private fetchAccountingRulesBasedOnAccountingPlan(): Observable<void> {
    this.dataLoaded.next(null);
    this.pagination = new DefaultPagination(this.perPage);
    const search = '';
    const orderBy = this.readOnly ? '' : '-active';
    this.pagination.resetPageIfNeeded({search, sort: orderBy});
    this.isAccountingRuleLoading = true;
    this.accountingRuleForm = this.formBuilder.group({accountingRules: this.formBuilder.array([])});
    return this.accountingService.getAccountingRules(
      this.accountingPlan?.id,
      9999,
      0,
      search,
      orderBy
    ).pipe(
      map(data => <AccountingRuleView[]>data.payload),
      map((accountingRuleViews: AccountingRuleView[]) => {
        this.initialAccountingRules = [];
        this.deletedRules = [];
        accountingRuleViews.forEach((rule, index) => {
          const tempDebitRules = [...rule.debitRules || []];
          const tempCreditRules = [...rule.creditRules || []];
          rule.creditRules = this.mapRulesToTagModel(tempCreditRules);
          rule.debitRules = this.mapRulesToTagModel(tempDebitRules);

          rule.index = index;
          rule.complementaryContractsList = (rule.complementaryContractIds || [])
            .map(id => this.complementaryContractList.find(node => node.id === id));

          if (!rule.accountingRuleVariabilities) {
            rule.accountingRuleVariabilities = [];
          }

          rule.accountingRuleVariabilities.sort((a, b) => a.orderNumber - b.orderNumber);

          this.initialAccountingRules.push(rule);
        });
        this.pagination.update(accountingRuleViews.length);
        this.dataLoaded.next(null);
        this.createAccountingRuleFormControlFromAccountingRules(this.initialAccountingRules);
        this.lastEditedIndex = null;
        this.addClickOutsideListener();
        this.isAccountingRuleLoading = false;
        this.cd.detectChanges();
        this.dataLoaded.next(null);
      }));
  }

  private filterPayArraysBasedOnInput(input: string, payArraysList: any[]) {
    payArraysList = [...this.originalPayArrayList];
    if (!isEmpty(input)) {
      payArraysList = [...this.originalPayArrayList];
      return payArraysList.filter(payArray => payArray.value.toLowerCase().includes(input.toLowerCase())
        || payArray.display.toLowerCase().includes(input.toLowerCase()))
        .sort((a, b) => ((a.value.toLowerCase().indexOf(input.toLowerCase())
          < b.value.toLowerCase().indexOf(input.toLowerCase()))
        || (a.display.toLowerCase().indexOf(input.toLowerCase())
          < b.display.toLowerCase().indexOf(input.toLowerCase())) ? -1 : 1));
    }
    return payArraysList;
  }

  private setFocusToNewRow(insertionIndex: number) {
    setTimeout(() => {
      const addedValue = this.accountingRules.at(insertionIndex).value;
      addedValue.editMode = true;
      this.accountingRules.at(insertionIndex).patchValue(addedValue);
      this.lastEditedIndex = insertionIndex;
      if (this.accountingRuleAccountInput) {
        this.accountingRuleAccountInput.last.nativeElement.focus();
      }
      this.cd.detectChanges();
    });
  }

  private filterAccountingRulesBasedOnInput() {
    const initialRules = [...this.initialAccountingRules];
    const params = createFiltersObj(this.filtersGroup);
    const filteredList = filterList(params, initialRules);
    this.createAccountingRuleFormControlFromAccountingRules(filteredList);
    if (Object.keys(params).find(key => !isEmpty(params[key]))) {
      this.pagination = new DefaultPagination(this.perPage);
      this.dataLoaded.next(null);
    }
    this.pagination.update(filteredList.length);
    this.cd.detectChanges();
  }

  private goToRelevantPageAndGetInsertionIndex(): number {
    const pageNumber = Math.floor(this.pagination.totalItems / this.pagination.perPage);
    const currentPage = this.pagination.totalItems <= this.pagination.perPage ? 0 : pageNumber;
    const lastIndex = this.initialAccountingRules.length;
    if (currentPage > 0) {
      for (let i = 0; i < currentPage; i++) {
        this.pagination.next();
      }
    }
    return lastIndex;
  }

  private goToNextPageIfNecessary(formIndex: number) {
    if (formIndex === this.pagination.perPage) {
      this.pagination.next();
    }
  }

  private uniqueStringValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const existingValues = this.accountingRules.getRawValue().map(ar => ar.account);
      if (control.value) {
        if (existingValues.filter(value => value === control.value).length > 1) {
          return {duplicateValue: true};
        }
      }
      return null;
    };
  }

  private createNewAccountingRule(insertionIndex: number) {
    const newAccountingRuleView = new AccountingRuleView();
    newAccountingRuleView.newRule = true;
    newAccountingRuleView.index = insertionIndex;
    newAccountingRuleView.active = true;
    newAccountingRuleView.editMode = true;
    return newAccountingRuleView;
  }

  private disableEditModeForUnSelectedRows(insertionIndex: number) {
    this.accountingRules.controls.forEach((ctrl, index) => {
      if (index !== insertionIndex) {
        const value = ctrl.value;
        value.editMode = false;
        ctrl.patchValue(ctrl.value);
        const listIndex = this.initialAccountingRules.findIndex(ar => ar.index === value.index);
        this.initialAccountingRules[listIndex] = value;
      }
    });
    this.cd.detectChanges();
  }

  private reCalculateIndex() {
    this.initialAccountingRules.forEach((accountingRule, index) => {
      accountingRule.index = index;
    });
  }

  private getDirtyValues(form: any) {
    const dirtyValues = {};

    Object.keys(form.controls)
      .forEach(key => {
        const currentControl = form.controls[key];

        if (currentControl.dirty) {
          if (currentControl.controls) {
            dirtyValues[key] = this.getDirtyValues(currentControl);
          } else {
            dirtyValues[key] = currentControl.value;
          }
        }
      });

    return dirtyValues;
  }

  private mapRulesToTagModel(rules: string[]): { display: string, value: string }[] {
    if (!rules || rules.length === 0) {
      return [];
    }
    return rules.map(rule => ({
      display: this.translate.instant(rule),
      value: rule,
      tooltipText: this.getTagModelTooltip(rule)
    }));
  }

  private mapTagModelsToRule(mappedRules: any[]): string[] {
    if (!mappedRules || mappedRules.length === 0) {
      return [];
    }
    return mappedRules.map(rule => isObject(rule) ? rule.value : rule);
  }

  private getTagModelTooltip(rule: string): string {
    const translatedRule = this.translate.instant(rule);
    return translatedRule === rule ? rule : `${translatedRule} - ${rule}`;
  }

  public canCancelBtnBeEnabled(): boolean {
    return this.formContainsChanges() && !this.executingActions;
  }

  public confirmResetFormTemplate() {
    this.confirmResetForm();
  }

  public submitForm() {
    this.updateAccountingRules();
  }

  public canSaveBtnBeEnabled(): boolean {
    return this.canSaveButtonBeEnabled() && !this.executingActions;
  }

  public openVariabilitiesEditor(event: Event, accountingRule: AccountingRuleView) {
    event.stopPropagation();
    this.modalService.openModalWithId('edit-event');
    this.initialVariabilities = cloneDeep(accountingRule.accountingRuleVariabilities);

    for (let i = 0; i < this.initialVariabilities.length; i++) {
      const accountingRuleVariability = this.initialVariabilities[i];
      this.selectedRegContractIndex[i] = this.paramOptions
        .find(param =>
          param.name === accountingRuleVariability.ruleTransId
        );
    }

    this.accountingRuleSelection = accountingRule;
  }

  public deleteRow(row: AccountingRuleVariability) {
    const index = this.accountingRuleSelection.accountingRuleVariabilities.indexOf(row);
    if (index !== -1) {
      // Remove the row from accountingRuleVariabilities
      this.accountingRuleSelection.accountingRuleVariabilities.splice(index, 1);

      // Remove the corresponding index from selectedRegContractIndex
      this.selectedRegContractIndex.splice(index, 1);
    }
  }

  public duplicateRow(row: AccountingRuleVariability) {
    const index = this.accountingRuleSelection?.accountingRuleVariabilities?.indexOf(row);
    if (index !== -1) {
      const plusOneOrder = this.accountingRuleSelection.accountingRuleVariabilities.length + 1;
      this.accountingRuleSelection.accountingRuleVariabilities.splice(index + 1, 0, { ...row });

      // Update the duplicated row's order number
      const duplicatedRow = this.accountingRuleSelection.accountingRuleVariabilities[index + 1];
      duplicatedRow.orderNumber = plusOneOrder;
      // Set id to null in order to create (else it would replace existing id)
      duplicatedRow.id = null;

      // Update selectedRegContractIndex by shifting the elements
      this.selectedRegContractIndex.splice(index + 1, 0, this.selectedRegContractIndex[index]);

      // Now assign the paramOptions to the newly duplicated row's selectedRegContractIndex
      this.selectedRegContractIndex[index + 1] = this.paramOptions
        .find(param => param.name === row.ruleTransId);
    }
  }

  public addRow() {
    this.selectedRegContractIndex[this.selectedRegContractIndex.length] = this.paramOptions[0];
    this.accountingRuleSelection?.accountingRuleVariabilities?.push({
      ruleId: this.accountingRuleSelection.id,
      account: '',
      accountName: '',
      ruleTransId: '',
      ruleOperator: '',
      ruleValue: '',
      orderNumber: this.accountingRuleSelection.accountingRuleVariabilities.length + 1
    });
  }

  public cancelVariabilitiesModalCommand(): Observable<ModalCommand> {
    this.cancelVariabilities();

    return of({
      state: ModalState.INVALID,
      message: this.translate.instant('LEAVE_REQUEST.cancellation'),
      duration: 500
    });
  }

  private cancelVariabilities(): void {
    const initialRuleValues = this.initialAccountingRules
      .filter(rule => rule.id === this.accountingRuleSelection.id)[0];
    initialRuleValues.accountingRuleVariabilities = this.initialVariabilities;
    this.accountingRuleSelection.accountingRuleVariabilities = this.initialVariabilities;

    this.selectedRegContractIndex = [];
  }

  public submitVariabilities(): Observable<ModalCommand> {
    this.saveAccountingRule(this.accountingRuleSelection);
    return of({
      state: ModalState.VALID,
      message: this.translate.instant('FORMS.success'),
      duration: 500
    });
  }

  public isBooleanCriteria(regContractIndex: RegContractIndexCriteria): boolean {
    return regContractIndex?.empZone?.type === 'BOOLEAN' || regContractIndex?.type === 'BOOLEAN';
  }

  public onChangeTransId(
    regContractIndex: RegContractIndexCriteria,
    accountingRuleVar: AccountingRuleVariability
  ) {
    accountingRuleVar.ruleTransId = regContractIndex.name;
    accountingRuleVar.dataIndex = regContractIndex.dataIndice;
    if (this.isBooleanCriteria(regContractIndex)) {
      accountingRuleVar.ruleValue = this.trueFalseOptions[0];
      accountingRuleVar.ruleOperator = this.operatorOptions[0].value;
    }
  }

  public onClosedModal() {
    this.cancelVariabilities();
  }

  public calculateColSpan() {
    this.colSpanTable = this.readOnly ? 0 : 1;
    if (this.enableVariabilities) {
      this.colSpanTable += 8;
    } else {
      this.colSpanTable += 7;
    }
  }
}
